import React, { useState } from "react";
import dayjs from "dayjs";
import { Button, Switch } from "antd";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiChevronUp, mdiAccount, mdiAirplaneLanding, mdiAirplaneTakeoff, mdiBedDouble, mdiCalendarMonth, mdiTranslate } from "@mdi/js";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import { PaxCounts, fareForPaxType } from "../components/ChooseYourTour";
import { LandTourPriceDate } from "../../../../interfaces/land-tour.interface";
import { FlightDetails, Passenger, effectivePassengers, flightSummary, passengerMissing, travellersSummary } from "./bookingUtils";
import PassengerDrawer from "./PassengerDrawer";
import { PAX_TYPES } from "../components/ChooseYourTour";
import useBreakpoint from "../../../../utils/useBreakpoint";

const longDate = (value?: string) => (value ? dayjs(value).format("DD MMM YYYY") : "");

interface Props {
  landTour: LandTourDetail;
  departDate: string;
  priceDate: LandTourPriceDate;
  paxCounts: PaxCounts;
  roomDisplay: string;
  language: string;
  flights: FlightDetails;
  passengers: Passenger[];
  countries: { value: string; label: string }[];
  willRegister: boolean;
  showErrors: boolean;
  onPassengerChange: (index: number, values: Partial<Passenger>) => void;
  onEditSelection: () => void;
}

export default function TravellersStep({
  landTour, departDate, priceDate, paxCounts, roomDisplay, language, flights, passengers, countries,
  willRegister, showErrors, onPassengerChange, onEditSelection,
}: Props) {
  const [editing, setEditing] = useState<number | null>(null);
  const [tabsOpen, setTabsOpen] = useState(false);
  const isMobile = useBreakpoint();
  const effective = effectivePassengers(passengers);

  const { outbound, inbound } = flightSummary(landTour, departDate, flights);

  const field = (label: string, value: string | undefined, missing: boolean) => (
    <div className="gt-kv" key={label}>
      <span>{label}</span>
      {value ? <strong>{value}</strong> : <strong className={missing && showErrors ? "is-missing" : "is-missing is-soft"}>Please Fill Up</strong>}
    </div>
  );

  const drawer = editing !== null && (
    <PassengerDrawer
      open
      index={editing}
      passenger={effective[editing]}
      landTour={landTour}
      departDate={departDate}
      fare={fareForPaxType(priceDate, paxCounts, passengers[editing].category)}
      countries={countries}
      willRegister={willRegister}
      onClose={() => setEditing(null)}
      firstContact={{ mobile: effective[0]?.mobile || "", email: effective[0]?.email || "" }}
      onSave={(values) => {
        onPassengerChange(editing, values);
        setEditing(null);
      }}
    />
  );

  if (isMobile) {
    return (
      <>
        <h2 className="gt-m-title">Please fill up traveller details</h2>
        <div className={`gt-m-tabs ${tabsOpen ? "is-open" : ""}`}>
          <div className="gt-m-tabs__list">
            {passengers.map((passenger, index) => (
              <button
                type="button"
                key={`tab-${index}`}
                onClick={() => document.getElementById(`gt-passenger-${index}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                Passenger {index + 1}
              </button>
            ))}
          </div>
          <button type="button" className="gt-m-tabs__toggle" onClick={() => setTabsOpen((open) => !open)} aria-label={tabsOpen ? "Collapse passengers" : "Expand passengers"}>
            <Icon path={tabsOpen ? mdiChevronUp : mdiChevronDown} size="24px" />
          </button>
        </div>

        {passengers.map((passenger, index) => {
          const shown = effective[index];
          const missing = passengerMissing(shown, index, landTour);
          const row = (label: string, value: string | undefined, key: string) => (
            <div className="gt-m-kv" key={label}>
              <span>{label}</span>
              {value ? <strong>{value}</strong> : <strong className="is-missing">Please Fill Up</strong>}
            </div>
          );
          return (
            <article className="gt-card gt-m-passenger" id={`gt-passenger-${index}`} key={`${passenger.category}-${index}`}>
              <div className="gt-card__head">
                <h4>Passenger {index + 1}</h4>
                <button type="button" className="gt-edit-btn" onClick={() => setEditing(index)}>Edit</button>
              </div>
              {row("Family name/Surname on Passport", shown.surname, "surname")}
              {row("Given name on Passport", shown.given_name, "given_name")}
              {row("Date of Birth", longDate(shown.date_of_birth), "date_of_birth")}
              {row("Mobile Number", shown.mobile, "mobile")}
              {row("Email", shown.email, "email")}
              {row("Nationality", shown.nationality, "nationality")}
              {index === 0 && (landTour.wechatQrcodeRequired || shown.wechat_qrcode) && row("WeChat QR Code", shown.wechat_qrcode ? shown.wechat_qrcode_name || "Uploaded" : "", "wechat")}
              {showErrors && missing.length > 0 && <p className="gt-field-error">Please complete the required details.</p>}
            </article>
          );
        })}
        {drawer}
      </>
    );
  }

  return (
    <>
      <section className="gt-card gt-details-card">
        <div className="gt-card__head">
          <h3 className="gt-card__title">Departure and Accomodation Details</h3>
          <button type="button" className="gt-edit-btn" onClick={onEditSelection}>Edit</button>
        </div>
        <div className="gt-icon-rows">
          <div><Icon path={mdiCalendarMonth} size="20px" /><span>Departure Date</span><strong>{longDate(departDate)}</strong></div>
          <div><Icon path={mdiAccount} size="20px" /><span>Travellers</span><strong>{travellersSummary(paxCounts)}</strong></div>
          <div><Icon path={mdiBedDouble} size="20px" /><span>Room Selection</span><strong>{roomDisplay}</strong></div>
          <div><Icon path={mdiTranslate} size="20px" /><span>Language</span><strong>{language}</strong></div>
        </div>

        {(
          <div className="gt-flight-summary">
            <h4>Flight Details</h4>
            {(
              <div><Icon path={mdiAirplaneTakeoff} size="20px" /><span>Outbound · {outbound.date ? longDate(outbound.date) : "Date to be confirmed"}</span><strong>{outbound.line}</strong></div>
            )}
            {(
              <div><Icon path={mdiAirplaneLanding} size="20px" /><span>Return · {inbound.date ? longDate(inbound.date) : "Date to be confirmed"}</span><strong>{inbound.line}</strong></div>
            )}
          </div>
        )}
      </section>

      <section className="gt-card">
        <h3 className="gt-card__title">Travellers Details</h3>
        {passengers.map((passenger, index) => {
          const shown = effective[index];
          const missing = passengerMissing(shown, index, landTour);
          const category = PAX_TYPES.find((type) => type.key === passenger.category);
          return (
            <article className="gt-passenger" key={`${passenger.category}-${index}`}>
              <div className="gt-card__head">
                <h4>Passenger {index + 1}<small>{category?.label}</small></h4>
                <button type="button" className="gt-edit-btn" onClick={() => setEditing(index)}>Edit</button>
              </div>
              {index > 0 && (
                <label className="gt-same-contact">
                  <Switch size="small" checked={!!passenger.same_as_first} onChange={(checked) => onPassengerChange(index, { same_as_first: checked })} />
                  Contact details same as Passenger 1
                </label>
              )}
              {field("Family name/Surname on Passport", shown.surname, missing.includes("surname"))}
              {field("Given name on Passport", shown.given_name, missing.includes("given_name"))}
              {field("Date of Birth", longDate(shown.date_of_birth), missing.includes("date_of_birth"))}
              {field("Mobile Number", shown.mobile, missing.includes("mobile"))}
              {field("Email", shown.email, missing.includes("email"))}
              {field("Nationality", shown.nationality, missing.includes("nationality"))}
              {index === 0 && (landTour.wechatQrcodeRequired || shown.wechat_qrcode) && field("WeChat QR Code", shown.wechat_qrcode ? shown.wechat_qrcode_name || "Uploaded" : "", missing.includes("wechat_qrcode"))}
            </article>
          );
        })}
      </section>

      {drawer}
    </>
  );
}
